import React, { useEffect, useState } from "react";
import {
    collection,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import * as XLSX from "xlsx";
import Loader from "./Loader";
import LocationMaster from "../admin/LocationMaster";
import Test from "./Test";

function PartStock() {
    const [Dpart, setDpart] = useState(0);
    const COLLECTION_NAME = "PART_STOCK";
    const RETURNS_COLLECTION = "DEFECTIVE_RETURNS";
    const CONSUMPTION_COLLECTION = "PART_CONSUMPTION";
    const LOCATIONS_COLLECTION = "LOCATION_MASTER";

    const [parts, setParts] = useState([]);
    const [returns, setReturns] = useState([]); // WAITING and SENT items
    const [consumption, setConsumption] = useState([]); // USAGE logs
    const [masterLocations, setMasterLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("GOOD"); // GOOD, DEFECTIVE, WAITING, HISTORY, CONSUMED, PLANNING, LOCATIONS

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [searchTerm, setSearchTerm] = useState("");

    // Modals
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showActionModal, setShowActionModal] = useState(false);
    const [showProcessModal, setShowProcessModal] = useState(false);
    const [showDispatchModal, setShowDispatchModal] = useState(false);
    const [showConsumeModal, setShowConsumeModal] = useState(false);
    const [showAddDefectiveModal, setShowAddDefectiveModal] = useState(false);

    // Selection
    const [selectedPart, setSelectedPart] = useState(null);
    const [selectedReturn, setSelectedReturn] = useState(null);
    const [actionType, setActionType] = useState(""); // ADD_STOCK, TO_DEFECTIVE

    // Forms
    const [newPartForm, setNewPartForm] = useState({
        name: "",
        partNumber: "",
        hsnSac: "",
        model: "",
        initialQty: 0,
        initialDefectiveQty: 0,
        location: "",
        remarks: "",
        purchasePrice: 0,
        sellingPrice: 0
    });
    const [addDefectiveForm, setAddDefectiveForm] = useState({
        partId: "",
        name: "",
        partNumber: "",
        model: "",
        location: "",
        qty: 1,
        ticketNumber: "",
        customerName: "",
        remarks: ""
    });
    const [editForm, setEditForm] = useState({}); // Generic edit form
    const [actionQty, setActionQty] = useState("");
    const [processForm, setProcessForm] = useState({ ticketNumber: "", customerName: "", qty: 1 });
    const [dispatchForm, setDispatchForm] = useState({ courierName: "", trackingNumber: "" });
    const [consumeForm, setConsumeForm] = useState({ ticketNumber: "", customerName: "", qty: 1, remarks: "" });

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [view]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const partsSnap = await getDocs(collection(db, COLLECTION_NAME));
            const partsList = partsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setParts(partsList);

            const returnsSnap = await getDocs(collection(db, RETURNS_COLLECTION));
            const returnsList = returnsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setReturns(returnsList);

            const consumptionSnap = await getDocs(collection(db, CONSUMPTION_COLLECTION));
            const consumptionList = consumptionSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setConsumption(consumptionList);

            const locationsSnap = await getDocs(collection(db, LOCATIONS_COLLECTION));
            let locationsList = locationsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

            // Seed initial locations if none exist
            if (locationsList.length === 0) {
                const initialLocs = ["GRPD", "GRAD", "DRPD", "DRAD", "DESP"];
                for (const loc of initialLocs) {
                    await addDoc(collection(db, LOCATIONS_COLLECTION), {
                        name: loc,
                        createdAt: Timestamp.now()
                    });
                }
                const newSnap = await getDocs(collection(db, LOCATIONS_COLLECTION));
                locationsList = newSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            }
            setMasterLocations(locationsList.sort((a, b) => a.name.localeCompare(b.name)));
        } catch (err) {
            console.error("Error loading data:", err);
        } finally {
            setLoading(false);
        }
    };

    // --- ACTIONS ---

    const handleAddNewPart = async () => {
        if (!newPartForm.name || !newPartForm.partNumber || !newPartForm.model || !newPartForm.location || !newPartForm.remarks) {
            return alert("All fields including Remarks and Location are required");
        }

        setLoading(true);
        try {
            await addDoc(collection(db, COLLECTION_NAME), {
                name: newPartForm.name,
                partNumber: newPartForm.partNumber || "",
                hsnSac: newPartForm.hsnSac || "",
                model: newPartForm.model || "",
                location: newPartForm.location || "",
                goodQty: Number(newPartForm.initialQty || 0),
                defectiveQty: Number(newPartForm.initialDefectiveQty || 0),
                purchasePrice: Number(newPartForm.purchasePrice || 0),
                sellingPrice: Number(newPartForm.sellingPrice || 0),
                remarks: newPartForm.remarks || "",
                type: "Goods",
                createdAt: Timestamp.now(),
            });

            // Sync with Item Master
            const itemsSnap = await getDocs(collection(db, "ITEM_MASTER"));
            const existingItem = itemsSnap.docs.find(d => {
                const item = d.data();
                return item.name.toLowerCase() === newPartForm.name.toLowerCase() && item.partNumber === newPartForm.partNumber;
            });

            const itemData = {
                name: newPartForm.name,
                partNumber: newPartForm.partNumber || "",
                hsnSac: newPartForm.hsnSac || "",
                type: "Goods",
                description: newPartForm.model || "",
                purchasePrice: Number(newPartForm.purchasePrice || 0),
                sellingPrice: Number(newPartForm.sellingPrice || 0),
                defaultRate: Number(newPartForm.sellingPrice || 0),
                unit: "Nos",
                taxRate: 18,
                updatedAt: Timestamp.now()
            };

            if (existingItem) {
                await updateDoc(doc(db, "ITEM_MASTER", existingItem.id), itemData);
            } else {
                await addDoc(collection(db, "ITEM_MASTER"), {
                    ...itemData,
                    createdAt: Timestamp.now()
                });
            }

            setShowAddModal(false);
            setNewPartForm({
                name: "",
                partNumber: "",
                hsnSac: "",
                model: "",
                initialQty: 0,
                initialDefectiveQty: 0,
                location: "",
                remarks: "",
                purchasePrice: 0,
                sellingPrice: 0
            });
            fetchData();
        } catch (err) {
            console.error("Error adding part:", err);
            alert("Failed to add part");
        } finally {
            setLoading(false);
        }
    };

    const executeAction = async () => {
        const qty = Number(actionQty);
        if (!qty || qty <= 0) return alert("Invalid Quantity");
        if (!selectedPart) return;

        setLoading(true);
        try {
            const ref = doc(db, COLLECTION_NAME, selectedPart.id);
            let updates = {};

            if (actionType === "ADD_STOCK") {
                updates = {
                    goodQty: (selectedPart.goodQty || 0) + qty
                };
            } else if (actionType === "TO_DEFECTIVE") {
                if (selectedPart.goodQty < qty) {
                    alert("Insufficient Good Stock");
                    setLoading(false);
                    return;
                }
                updates = {
                    goodQty: (selectedPart.goodQty || 0) - qty,
                    defectiveQty: (selectedPart.defectiveQty || 0) + qty
                };
            }

            await updateDoc(ref, updates);
            setShowActionModal(false);
            setActionQty("");
            fetchData();
        } catch (err) {
            console.error("Action failed:", err);
            alert("Failed to update stock");
        } finally {
            setLoading(false);
        }
    };

    const handleAddDefectiveStock = async () => {
        const qty = Number(addDefectiveForm.qty);
        if (!qty || qty <= 0) return alert("Invalid quantity");

        if (!addDefectiveForm.ticketNumber || !addDefectiveForm.customerName) {
            return alert("Ticket Number and Customer Name are required");
        }

        if (!addDefectiveForm.partId) {
            if (!addDefectiveForm.name || !addDefectiveForm.partNumber || !addDefectiveForm.model || !addDefectiveForm.location) {
                return alert("All part details (Name, Number, Model, Location) are required for manual entry");
            }
        }

        setLoading(true);
        try {
            let newPartId = "";
            let finalPartName = "";
            let finalPartNumber = "";
            let finalModel = "";

            if (!addDefectiveForm.partId) {
                // Check if part with same name/number exists to avoid duplicates
                const existing = parts.find(p =>
                    p.name.toLowerCase() === addDefectiveForm.name.toLowerCase() &&
                    p.partNumber === addDefectiveForm.partNumber
                );

                if (existing) {
                    const ref = doc(db, COLLECTION_NAME, existing.id);
                    await updateDoc(ref, {
                        defectiveQty: (existing.defectiveQty || 0) + qty,
                        remarks: addDefectiveForm.remarks || ""
                    });
                    newPartId = existing.id;
                    finalPartName = existing.name;
                    finalPartNumber = existing.partNumber;
                    finalModel = existing.model;
                } else {
                    // Create new
                    if (!addDefectiveForm.name) {
                        alert("Part Name is required if no existing part is selected");
                        setLoading(false);
                        return;
                    }
                    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
                        name: addDefectiveForm.name,
                        partNumber: addDefectiveForm.partNumber || "",
                        model: addDefectiveForm.model || "",
                        location: addDefectiveForm.location || "",
                        goodQty: 0,
                        defectiveQty: qty,
                        remarks: addDefectiveForm.remarks || "",
                        createdAt: Timestamp.now(),
                    });
                    newPartId = docRef.id;
                    finalPartName = addDefectiveForm.name;
                    finalPartNumber = addDefectiveForm.partNumber;
                    finalModel = addDefectiveForm.model;
                }
            } else {
                // Update existing by ID
                const part = parts.find(p => p.id === addDefectiveForm.partId);
                const ref = doc(db, COLLECTION_NAME, addDefectiveForm.partId);
                await updateDoc(ref, {
                    defectiveQty: (part.defectiveQty || 0) + qty,
                    remarks: addDefectiveForm.remarks || ""
                });
                newPartId = part.id;
                finalPartName = part.name;
                finalPartNumber = part.partNumber;
                finalModel = part.model;
            }

            // Always add to DEFECTIVE list first - user can process return from DEFECTIVE view later
            setShowAddDefectiveModal(false);
            setAddDefectiveForm({ partId: "", name: "", partNumber: "", model: "", location: "", qty: 1, ticketNumber: "", customerName: "", remarks: "" });
            setView("DEFECTIVE"); // Auto-switch to DEFECTIVE view
            fetchData();
            alert("Defective stock recorded successfully");
        } catch (err) {
            console.error("Add failed:", err);
            alert("Failed to add defective stock");
        } finally {
            setLoading(false);
        }
    };

    const handleConsumePart = async () => {
        if (!consumeForm.ticketNumber || !consumeForm.customerName) {
            return alert("Ticket Number and Customer Name are required");
        }
        const qty = Number(consumeForm.qty);
        if (!qty || qty <= 0 || qty > selectedPart.goodQty) {
            return alert("Invalid quantity or insufficient stock");
        }

        setLoading(true);
        try {
            // 1. Decrement Stock
            const partRef = doc(db, COLLECTION_NAME, selectedPart.id);
            await updateDoc(partRef, {
                goodQty: (selectedPart.goodQty || 0) - qty
            });

            // 2. Create Consumption Doc
            const consumeData = {
                partId: selectedPart.id,
                partName: selectedPart.name,
                partNumber: selectedPart.partNumber || "",
                model: selectedPart.model || "",
                qty: qty,
                ticketNumber: consumeForm.ticketNumber,
                customerName: consumeForm.customerName,
                remarks: consumeForm.remarks || "",
                consumedAt: Timestamp.now()
            };

            await addDoc(collection(db, CONSUMPTION_COLLECTION), consumeData);

            setShowConsumeModal(false);
            fetchData();
            alert("Part consumed successfully");
        } catch (err) {
            console.error("Consumption failed:", err);
            alert("Failed to consume part");
        } finally {
            setLoading(false);
        }
    };

    const handleEditSave = async () => {
        setLoading(true);
        try {
            let targetCollection = COLLECTION_NAME;
            if (view === "WAITING" || view === "HISTORY") targetCollection = RETURNS_COLLECTION;
            if (view === "CONSUMED") targetCollection = CONSUMPTION_COLLECTION;

            const ref = doc(db, targetCollection, editForm.id);
            const { id, ...payload } = editForm; // remove id from payload

            // Format numbers
            if (payload.goodQty !== undefined) payload.goodQty = Number(payload.goodQty);
            if (payload.defectiveQty !== undefined) payload.defectiveQty = Number(payload.defectiveQty);
            if (payload.qty !== undefined) payload.qty = Number(payload.qty);
            if (payload.purchasePrice !== undefined) payload.purchasePrice = Number(payload.purchasePrice);
            if (payload.sellingPrice !== undefined) payload.sellingPrice = Number(payload.sellingPrice);

            if (targetCollection === COLLECTION_NAME && !payload.type) {
                payload.type = "Goods";
            }

            await updateDoc(ref, payload);

            // Sync with Item Master if it's a primary part update
            if (targetCollection === COLLECTION_NAME) {
                const itemsSnap = await getDocs(collection(db, "ITEM_MASTER"));
                const existingItem = itemsSnap.docs.find(d => {
                    const item = d.data();
                    const matchByPartNo = payload.partNumber && item.partNumber === payload.partNumber;
                    const matchByName = item.name.toLowerCase().trim() === (payload.name || "").toLowerCase().trim();
                    return matchByPartNo || matchByName;
                });

                const itemData = {
                    name: payload.name || "",
                    partNumber: payload.partNumber || "",
                    hsnSac: payload.hsnSac || "",
                    type: "Goods",
                    description: payload.model || "",
                    purchasePrice: Number(payload.purchasePrice || 0),
                    sellingPrice: Number(payload.sellingPrice || 0),
                    defaultRate: Number(payload.sellingPrice || 0),
                    unit: "Nos",
                    taxRate: 18,
                    updatedAt: Timestamp.now()
                };

                if (existingItem) {
                    await updateDoc(doc(db, "ITEM_MASTER", existingItem.id), itemData);
                } else {
                    await addDoc(collection(db, "ITEM_MASTER"), {
                        ...itemData,
                        createdAt: Timestamp.now()
                    });
                }
            }

            setShowEditModal(false);
            fetchData();
            alert("Updated and synced with Item Master successfully");
        } catch (err) {
            console.error("Update failed:", err);
            alert("Failed to update");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRecord = async (item) => {
        if (!window.confirm("Are you sure you want to delete this record? This will NOT revert stock changes.")) return;

        setLoading(true);
        try {
            let targetCollection = COLLECTION_NAME;
            if (view === "WAITING" || view === "HISTORY") targetCollection = RETURNS_COLLECTION;
            if (view === "CONSUMED") targetCollection = CONSUMPTION_COLLECTION;

            await deleteDoc(doc(db, targetCollection, item.id));
            fetchData();
            alert("Deleted successfully");
        } catch (err) {
            console.error("Delete failed:", err);
            alert("Failed to delete");
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        const data = filteredItems().map(item => {
            const row = {
                "Part Name": item.name || item.partName,
                "Part Number": item.partNumber || "-",
                "Model / Spec": item.model || "-",
                "Location": item.location || "-",
                "Remarks": item.remarks || "-"
            };

            if (view === "GOOD") row["Good Qty"] = item.goodQty || 0;
            if (view === "DEFECTIVE") row["Defective Qty"] = item.defectiveQty || 0;

            if (view === "WAITING" || view === "HISTORY" || view === "CONSUMED") {
                row["Qty"] = item.qty || 0;
                row["Customer Name"] = item.customerName || "-";
                row["Ticket Number"] = item.ticketNumber || "-";
            }

            if (view === "HISTORY") {
                row["Courier"] = item.courierName || "-";
                row["Tracking"] = item.trackingNumber || "-";
                row["Sent Date"] = item.sentAt?.toDate().toLocaleDateString() || "-";
            }

            if (view === "CONSUMED") {
                row["Consumed Date"] = item.consumedAt?.toDate().toLocaleDateString() || "-";
            }

            return row;
        });

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "StockData");
        XLSX.writeFile(wb, `PartStock_${view}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const handleProcessReturn = async () => {
        if (!processForm.ticketNumber || !processForm.customerName) {
            return alert("Ticket Number and Customer Name are required");
        }
        const qty = Number(processForm.qty);
        if (!qty || qty <= 0 || qty > selectedPart.defectiveQty) {
            return alert("Invalid quantity");
        }

        setLoading(true);
        try {
            // 1. Decrement Stock
            const partRef = doc(db, COLLECTION_NAME, selectedPart.id);
            await updateDoc(partRef, {
                defectiveQty: (selectedPart.defectiveQty || 0) - qty
            });

            // 2. Create Return Doc
            const returnData = {
                partId: selectedPart.id,
                partName: selectedPart.name,
                partNumber: selectedPart.partNumber || "",
                model: selectedPart.model || "",
                qty: qty,
                ticketNumber: processForm.ticketNumber,
                customerName: processForm.customerName,
                status: "WAITING",
                processedAt: Timestamp.now(),
                remarks: selectedPart.remarks || ""
            };

            const docRef = await addDoc(collection(db, RETURNS_COLLECTION), returnData);

            setShowProcessModal(false);
            fetchData();

            // 3. Trigger Print
            handlePrintTag({ id: docRef.id, ...returnData });

        } catch (err) {
            console.error("Process failed:", err);
            alert("Failed to process return");
        } finally {
            setLoading(false);
        }
    };

    const handleDispatch = async () => {
        if (!dispatchForm.courierName) return alert("Courier Details required");

        setLoading(true);
        try {
            const ref = doc(db, RETURNS_COLLECTION, selectedReturn.id);
            await updateDoc(ref, {
                status: "SENT",
                courierName: dispatchForm.courierName,
                trackingNumber: dispatchForm.trackingNumber || "",
                sentAt: Timestamp.now()
            });

            setShowDispatchModal(false);
            setDispatchForm({ courierName: "", trackingNumber: "" });
            setView("WAITING"); // Switch to WAITING view
            fetchData();
        } catch (err) {
            console.error("Dispatch failed:", err);
            alert("Failed to mark as sent");
        } finally {
            setLoading(false);
        }
    };

    const handlePrintTag = (data) => {
        const printWindow = window.open("", "_blank");
        const html = `
      <html>
        <head>
          <title>Defective Part Tag</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .tag {
              border: 3px solid black;
              padding: 15px;
              width: 400px;
              background-color: #ff8c00; /* Orange background */
              color: black;
            }
            .header {
              text-align: center;
              font-weight: bold;
              font-size: 24px;
              border-bottom: 2px solid black;
              margin-bottom: 10px;
              padding-bottom: 5px;
            }
            .field {
              margin-bottom: 8px;
              border-bottom: 1px solid black;
              padding-bottom: 3px;
              display: flex;
            }
            .label { font-weight: bold; width: 150px; }
            .value { flex: 1; }
            .footer {
              margin-top: 15px;
              font-size: 14px;
              display: flex;
              justify-content: space-between;
              font-weight: bold;
            }
            @media print {
              body { padding: 0; background: none; }
              .tag { background-color: #ff8c00 !important; -webkit-print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="tag">
            <div class="header">Defective Part Tag</div>
            <div class="field"><span class="label">Ticket / Call No:</span><span class="value">${data.ticketNumber}</span></div>
            <div class="field"><span class="label">Customer Name:</span><span class="value">${data.customerName}</span></div>
            <div class="field"><span class="label">Part Name:</span><span class="value">${data.partName}</span></div>
            <div class="field"><span class="label">Part Number:</span><span class="value">${data.partNumber || "-"}</span></div>
            <div class="field"><span class="label">Model / Spec:</span><span class="value">${data.model || "-"}</span></div>
            <div class="field"><span class="label">Quantity:</span><span class="value">${data.qty}</span></div>
            <div class="field"><span class="label">Remarks:</span><span class="value">${data.remarks || "-"}</span></div>
            <div class="footer">
                <span>BLUE STAR</span>
                <span style="font-size: 10px;">ASIAN CLIMATE CONTROLS</span>
            </div>
          </div>
          <script>
            window.onload = function() { window.print(); window.close(); }
          </script>
        </body>
      </html>
    `;
        printWindow.document.write(html);
        printWindow.document.close();
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this part entirely?")) return;
        try {
            await deleteDoc(doc(db, COLLECTION_NAME, id));
            fetchData();
        } catch (e) {
            console.error(e);
            alert("Failed delete");
        }
    }

    // --- RENDER HELPERS ---

    const filteredItems = () => {
        let list = [];
        if (view === "GOOD") list = parts.filter(p => p.type !== "Services");
        else if (view === "DEFECTIVE") list = parts.filter(p => p.type !== "Services" && (p.defectiveQty || 0) > 0);
        else if (view === "WAITING") list = returns.filter(r => r.status === "WAITING");
        else if (view === "HISTORY") list = returns.filter(r => r.status === "SENT");
        else if (view === "CONSUMED") list = consumption;

        if (searchTerm) {
            const lowSearch = searchTerm.toLowerCase();
            list = list.filter(item =>
                (item.name || item.partName || "").toLowerCase().includes(lowSearch) ||
                (item.partNumber || "").toLowerCase().includes(lowSearch) ||
                (item.model || "").toLowerCase().includes(lowSearch) ||
                (item.customerName || "").toLowerCase().includes(lowSearch) ||
                (item.ticketNumber || "").toLowerCase().includes(lowSearch)
            );
        }

        return list;
    };

    const allFiltered = filteredItems();
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const paginatedItems = allFiltered.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(allFiltered.length / itemsPerPage);

    const openEdit = (item) => {
        setEditForm({ ...item });
        setShowEditModal(true);
    };

    const openConsume = (part) => {
        setSelectedPart(part);
        setConsumeForm({ ticketNumber: "", customerName: "", qty: 1, remarks: "" });
        setShowConsumeModal(true);
    };

    const openAction = (part, type) => {
        setSelectedPart(part);
        setActionType(type);
        setActionQty("");
        setShowActionModal(true);
    };

    const openProcess = (part) => {
        setSelectedPart(part);
        setProcessForm({
            ticketNumber: part.ticketNumber || "",
            customerName: part.customerName || "",
            qty: part.defectiveQty || 1
        });
        setShowProcessModal(true);
    };

    const openDispatch = (ret) => {
        setSelectedReturn(ret);
        setDispatchForm({ courierName: "", trackingNumber: "" });
        setShowDispatchModal(true);
    };

    return (
        <div className="container-fluid mt-4 px-4 pb-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="mb-0">Part Stock Management</h3>
                <div className="d-flex gap-2">
                    <div className="flex-grow-1 mx-3">
                        <div className="input-group">
                            <span className="input-group-text bg-white border-end-0">
                                <i className="bi bi-search text-muted"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control border-start-0 ps-0"
                                placeholder="Search by name, part #, model, customer or ticket..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button
                                    className="btn btn-outline-secondary border-start-0"
                                    onClick={() => setSearchTerm("")}
                                >
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            )}
                        </div>
                    </div>
                    {view === "PLANNING" && (
                        <button className="btn btn-outline-primary" onClick={fetchData}>
                            <i className="bi bi-arrow-clockwise me-1"></i> Refresh Data
                        </button>
                    )}
                    <button className="btn btn-outline-dark" onClick={handleExport}>
                        Export to Excel
                    </button>
                    {view === "DEFECTIVE" && (
                        <button className="btn btn-danger" onClick={() => setShowAddDefectiveModal(true)}>
                            + Add Defective Part
                        </button>
                    )}
                    {(view !== "DEFECTIVE" && view !== "PLANNING") && (
                        <button className="btn btn-primary" onClick={() => setShowAddModal(
                            true)}>
                            + Add New Part
                        </button>
                    )}
                </div>
            </div>

            <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                    <button
                        className={`nav-link ${view === "GOOD" ? "active fw-bold" : ""}`}
                        onClick={() => setView("GOOD")}
                    >
                        Good Stock ({parts.length})
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${view === "DEFECTIVE" ? "active fw-bold text-danger" : "text-danger"}`}
                        onClick={() => setView("DEFECTIVE")}
                    >
                        Defective Stock ({parts.filter(p => (p.defectiveQty || 0) > 0).length})
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${view === "WAITING" ? "active fw-bold text-warning" : "text-warning"}`}
                        onClick={() => setView("WAITING")}
                    >
                        Waiting for Dispatch ({returns.filter(r => r.status === "WAITING").length})
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${view === "HISTORY" ? "active fw-bold text-primary" : "text-primary"}`}
                        onClick={() => setView("HISTORY")}
                    >
                        Dispatch History ({returns.filter(r => r.status === "SENT").length})
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${view === "CONSUMED" ? "active fw-bold text-success" : "text-success"}`}
                        onClick={() => setView("CONSUMED")}
                    >
                        Consumption History ({consumption.length})
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${view === "PLANNING" ? "active fw-bold" : ""}`}
                        onClick={() => setView("PLANNING")}
                    >
                        MSL Planning
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${view === "LOCATIONS" ? "active fw-bold text-dark" : "text-dark"}`}
                        onClick={() => setView("LOCATIONS")}
                    >
                        Manage Locations
                    </button>
                </li>
            </ul>

            {loading && <Loader />}

            {!loading && (
                <div className="table-responsive">
                    {view === "LOCATIONS" ? (
                        <LocationMaster onUpdate={fetchData} />
                    ) : view === "PLANNING" ? (
                        <table className="table table-bordered table-hover align-middle shadow-sm">
                            <thead className="table-dark">
                                <tr>
                                    <th>Part Details</th>
                                    <th className="text-center">Available Stock (Good)</th>
                                    <th className="text-center">Defective Stock</th>
                                    <th className="text-center">90-Day Consumption</th>
                                    <th className="text-center">Suggested MSL</th>
                                    <th className="text-center">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parts.map((part) => {
                                    const ninetyDaysAgo = new Date();
                                    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

                                    const totalConsumed = consumption
                                        .filter(c => c.partId === part.id && c.consumedAt?.toDate() >= ninetyDaysAgo)
                                        .reduce((acc, curr) => acc + (curr.qty || 0), 0);

                                    const suggestedMSL = totalConsumed;
                                    const available = part.goodQty || 0;

                                    let status = { text: "Balanced", color: "bg-success text-white" };
                                    if (available === 0 && suggestedMSL > 0) status = { text: "CRITICAL", color: "bg-danger text-white animate__animated animate__flash animate__infinite" };
                                    else if (available < suggestedMSL * 0.5) status = { text: "LOW STOCK", color: "bg-danger text-white" };
                                    else if (available < suggestedMSL) status = { text: "Below MSL", color: "bg-warning text-dark" };
                                    else if (available > suggestedMSL * 2 && suggestedMSL > 0) status = { text: "OVERSTOCK", color: "bg-info text-white" };

                                    return (
                                        <tr key={part.id}>
                                            <td>
                                                <div className="fw-bold">{part.name}</div>
                                                <div className="small text-muted">{part.partNumber} | {part.model}</div>
                                                <div className="small text-primary fst-italic">{part.location}</div>
                                            </td>
                                            <td className="text-center">
                                                <span className={`fs-5 badge ${available > 0 ? "bg-success" : "bg-light text-dark border"}`}>
                                                    {available}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <span className={`badge ${part.defectiveQty > 0 ? "bg-danger" : "bg-light text-muted"}`}>
                                                    {part.defectiveQty || 0}
                                                </span>
                                            </td>
                                            <td className="text-center fw-bold text-primary">
                                                {totalConsumed}
                                            </td>
                                            <td className="text-center fw-bold">
                                                <div className="border rounded p-1 bg-light">
                                                    {suggestedMSL}
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <span className={`badge rounded-pill px-3 py-2 ${status.color}`}>
                                                    {status.text}
                                                </span>
                                                {available < suggestedMSL && (
                                                    <div className="small text-danger mt-1 fw-bold">
                                                        Need: {suggestedMSL - available}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {parts.length === 0 && (
                                    <tr><td colSpan="6" className="text-center text-muted py-4">No parts cataloged to plan MSL.</td></tr>
                                )}
                            </tbody>
                        </table>
                    ) : (
                        <table className="table table-bordered table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    {(view === "HISTORY" || view === "CONSUMED") && <th>Date</th>}
                                    <th>Part Number</th>
                                    <th>Part Name</th>
                                    <th>Model / Spec</th>
                                    <th>Location</th>
                                    <th>Remarks</th>
                                    {(view === "DEFECTIVE" || view === "WAITING" || view === "HISTORY" || view === "CONSUMED") && <th>Customer / Ticket</th>}
                                    {view === "HISTORY" && <th>Courier / Tracking</th>}
                                    {view === "GOOD" && <th className="text-center bg-info text-white">Selling Price</th>}
                                    {view === "GOOD" && <th className="text-center bg-success text-white">Good Qty</th>}
                                    {(view === "DEFECTIVE" || view === "WAITING" || view === "HISTORY" || view === "CONSUMED") && <th className="text-center bg-danger text-white">Qty</th>}
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedItems.map((item) => (
                                    <tr key={item.id}>
                                        {(view === "HISTORY" || view === "CONSUMED") && (
                                            <td>{(item.sentAt || item.consumedAt)?.toDate().toLocaleDateString()}</td>
                                        )}
                                        <td>{item.partNumber || "-"}</td>
                                        <td className="fw-bold">{item.name || item.partName}</td>
                                        <td>{item.model || "-"}</td>
                                        <td className="text-primary fw-bold small">{item.location || "-"}</td>
                                        <td>{item.remarks || "-"}</td>

                                        {(view === "DEFECTIVE" || view === "WAITING" || view === "HISTORY" || view === "CONSUMED") && (
                                            <td>
                                                <div className="small fw-bold">{item.customerName || "-"}</div>
                                                <div className="text-muted small">Tkt: {item.ticketNumber || "-"}</div>
                                            </td>
                                        )}

                                        {view === "HISTORY" && (
                                            <td>
                                                <div className="small fw-bold">{item.courierName}</div>
                                                <div className="text-muted small">{item.trackingNumber}</div>
                                            </td>
                                        )}

                                        {view === "GOOD" && (
                                            <td className="text-center fw-bold text-primary">
                                                ₹{(item.sellingPrice || 0).toLocaleString()}
                                            </td>
                                        )}
                                        {view === "GOOD" && (
                                            <td className="text-center fs-5">
                                                <span className={`badge ${item.goodQty > 0 ? "bg-success" : "bg-secondary"}`}>
                                                    {item.goodQty || 0}
                                                </span>
                                            </td>
                                        )}

                                        {(view === "DEFECTIVE" || view === "WAITING" || view === "HISTORY" || view === "CONSUMED") && (
                                            <td className="text-center fs-5">
                                                <span className="badge bg-danger">
                                                    {item.defectiveQty || item.qty || 0}
                                                </span>
                                            </td>
                                        )}

                                        <td>
                                            <div className="d-flex gap-2">
                                                {view === "GOOD" && (
                                                    <>
                                                        <button
                                                            className="btn btn-sm btn-outline-success"
                                                            onClick={() => openAction(item, "ADD_STOCK")}
                                                        >
                                                            + Add Stock
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-success"
                                                            onClick={() => openConsume(item)}
                                                            disabled={!item.goodQty}
                                                        >
                                                            🔧 Consume
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => openAction(item, "TO_DEFECTIVE")}
                                                            disabled={!item.goodQty}
                                                        >
                                                            ⚠ Mark Defective
                                                        </button>
                                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => openEdit(item)}>✏</button>
                                                        <button className="btn btn-sm btn-light text-muted" onClick={() => handleDelete(item.id)}>🗑</button>
                                                    </>
                                                )}

                                                {view === "DEFECTIVE" && (
                                                    <>
                                                        <button
                                                            className="btn btn-sm btn-warning"
                                                            onClick={() => openProcess(item)}
                                                        >
                                                            📋 Process Return
                                                        </button>
                                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => openEdit(item)}>✏</button>
                                                        <button className="btn btn-sm btn-light text-muted" onClick={() => handleDelete(item.id)}>🗑</button>
                                                    </>
                                                )}

                                                {view === "WAITING" && (
                                                    <>
                                                        <button
                                                            className="btn btn-sm btn-outline-dark"
                                                            onClick={() => handlePrintTag(item)}
                                                        >
                                                            🖨 Reprint Tag
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-success"
                                                            onClick={() => openDispatch(item)}
                                                        >
                                                            🚚 Sent to RPC
                                                        </button>
                                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => openEdit(item)}>✏</button>
                                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteRecord(item)}>🗑</button>
                                                    </>
                                                )}

                                                {view === "HISTORY" && (
                                                    <>
                                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => openEdit(item)}>✏</button>
                                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteRecord(item)}>🗑</button>
                                                    </>
                                                )}

                                                {view === "CONSUMED" && (
                                                    <>
                                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => openEdit(item)}>✏</button>
                                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteRecord(item)}>🗑</button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {paginatedItems.length === 0 && (
                                    <tr><td colSpan="10" className="text-center text-muted">No records found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    {/* PAGINATION CONTROLS */}
                    {allFiltered.length > 0 && (
                        <div className="d-flex justify-content-between align-items-center mt-3 mb-5 px-2">
                            <div className="d-flex align-items-center gap-2">
                                <span>Show</span>
                                <select
                                    className="form-select form-select-sm"
                                    style={{ width: "auto" }}
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                                <span>entries</span>
                                <span className="ms-3 text-muted small">
                                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, allFiltered.length)} of {allFiltered.length}
                                </span>
                            </div>

                            <nav>
                                <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                        <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>Previous</button>
                                    </li>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => {
                                        if (p === 1 || p === totalPages || (p >= currentPage - 2 && p <= currentPage + 2)) {
                                            return (
                                                <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                                                    <button className="page-link" onClick={() => setCurrentPage(p)}>{p}</button>
                                                </li>
                                            );
                                        } else if (p === currentPage - 3 || p === currentPage + 3) {
                                            return <li key={p} className="page-item disabled"><span className="page-link">...</span></li>;
                                        }
                                        return null;
                                    })}
                                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                        <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </div>
            )}

            {/* ADD MODAL */}
            {showAddModal && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add New Part</h5>
                                <button className="btn-close" onClick={() => setShowAddModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold">Part Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newPartForm.name}
                                            onChange={(e) => setNewPartForm({ ...newPartForm, name: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold">Part Number</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newPartForm.partNumber}
                                            onChange={(e) => setNewPartForm({ ...newPartForm, partNumber: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold">HSN/SAC Code</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newPartForm.hsnSac}
                                            onChange={(e) => setNewPartForm({ ...newPartForm, hsnSac: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold">Model / Description</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newPartForm.model}
                                            onChange={(e) => setNewPartForm({ ...newPartForm, model: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="fw-bold">Part Location</label>
                                    <select
                                        className="form-select"
                                        value={newPartForm.location}
                                        onChange={e => setNewPartForm({ ...newPartForm, location: e.target.value })}
                                    >
                                        <option value="">-- Select Location --</option>
                                        {masterLocations.map(loc => (
                                            <option key={loc.id} value={loc.name}>{loc.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="fw-bold">Remarks</label>
                                    <textarea
                                        className="form-control"
                                        placeholder="Required"
                                        value={newPartForm.remarks}
                                        onChange={e => setNewPartForm({ ...newPartForm, remarks: e.target.value })}
                                    />
                                </div>
                                <div className="row">
                                    <div className="col-4 mb-3">
                                        <label className="fw-bold">Initial Good Qty</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={newPartForm.initialQty}
                                            onChange={e => setNewPartForm({ ...newPartForm, initialQty: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-4 mb-3">
                                        <label className="fw-bold">Purchase Price</label>
                                        <input
                                            type="number"
                                            className="form-control border-danger"
                                            value={newPartForm.purchasePrice}
                                            onChange={e => setNewPartForm({ ...newPartForm, purchasePrice: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-4 mb-3">
                                        <label className="fw-bold">Selling Price</label>
                                        <input
                                            type="number"
                                            className="form-control border-success"
                                            value={newPartForm.sellingPrice}
                                            onChange={e => setNewPartForm({ ...newPartForm, sellingPrice: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-12 mb-3">
                                        <div className={`p-2 rounded text-center fw-bold ${(newPartForm.sellingPrice - newPartForm.purchasePrice) >= 0 ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                                            Estimated Profit: ₹{(newPartForm.sellingPrice - newPartForm.purchasePrice).toLocaleString()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={handleAddNewPart}>Save Part</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ACTION MODAL (Good/Defective Move) */}
            {showActionModal && selectedPart && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {actionType === "ADD_STOCK" && "Add New Stock"}
                                    {actionType === "TO_DEFECTIVE" && "Mark as Defective"}
                                </h5>
                                <button className="btn-close" onClick={() => setShowActionModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <p className="fw-bold">{selectedPart.name} ({selectedPart.model})</p>
                                <div className="mb-3">
                                    <label>Quantity</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        autoFocus
                                        value={actionQty}
                                        onChange={e => setActionQty(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowActionModal(false)}>Cancel</button>
                                <button className="btn btn-success" onClick={executeAction}>Confirm</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* PROCESS MODAL (Defective -> Waiting) */}
            {showProcessModal && selectedPart && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title text-warning">Process Defective Return</h5>
                                <button className="btn-close" onClick={() => setShowProcessModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <p><strong>Part:</strong> {selectedPart.name} ({selectedPart.partNumber})</p>
                                <div className="row g-3">
                                    <div className="col-md-6 text-center">
                                        <label className="d-block small text-muted">Max Defective Stock</label>
                                        <span className="badge bg-danger fs-5">{selectedPart.defectiveQty}</span>
                                    </div>
                                    <div className="col-md-6">
                                        <label>Returning Qty</label>
                                        <input type="number" className="form-control" value={processForm.qty} onChange={e => setProcessForm({ ...processForm, qty: e.target.value })} />
                                    </div>
                                    <div className="col-12">
                                        <label>Ticket / Call Number</label>
                                        <input className="form-control" placeholder="Required" value={processForm.ticketNumber} onChange={e => setProcessForm({ ...processForm, ticketNumber: e.target.value })} />
                                    </div>
                                    <div className="col-12">
                                        <label>Customer Name</label>
                                        <input className="form-control" placeholder="Required" value={processForm.customerName} onChange={e => setProcessForm({ ...processForm, customerName: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowProcessModal(false)}>Cancel</button>
                                <button className="btn btn-warning" onClick={handleProcessReturn}>Print Tag & Save</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* DISPATCH MODAL (Waiting -> Sent) */}
            {showDispatchModal && selectedReturn && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title text-success">Sending to RPC</h5>
                                <button className="btn-close" onClick={() => setShowDispatchModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <p><strong>Part:</strong> {selectedReturn.partName} x {selectedReturn.qty}</p>
                                <div className="mb-3">
                                    <label>Courier / Agency Name</label>
                                    <input className="form-control" autoFocus placeholder="e.g. Blue Dart, Professional" value={dispatchForm.courierName} onChange={e => setDispatchForm({ ...dispatchForm, courierName: e.target.value })} />
                                </div>
                                <div className="mb-3">
                                    <label>Tracking / Docket / Lorry No.</label>
                                    <input className="form-control" value={dispatchForm.trackingNumber} onChange={e => setDispatchForm({ ...dispatchForm, trackingNumber: e.target.value })} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowDispatchModal(false)}>Cancel</button>
                                <button className="btn btn-success" onClick={handleDispatch}>Confirm & Mark Sent</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ADD DEFECTIVE MODAL */}
            {showAddDefectiveModal && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content border-danger">
                            <div className="modal-header bg-danger text-white">
                                <h5 className="modal-title">Add Defective Stock</h5>
                                <button className="btn-close btn-close-white" onClick={() => setShowAddDefectiveModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <p className="small text-muted mb-3">You can either select an existing part or enter details manually for a new entry.</p>
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className="fw-bold">Select Existing Part (Optional)</label>
                                        <select
                                            className="form-select border-primary"
                                            value={addDefectiveForm.partId}
                                            onChange={e => {
                                                const selected = parts.find(p => p.id === e.target.value);
                                                if (selected) {
                                                    setAddDefectiveForm({
                                                        ...addDefectiveForm,
                                                        partId: selected.id,
                                                        name: selected.name,
                                                        partNumber: selected.partNumber,
                                                        model: selected.model,
                                                        location: selected.location
                                                    });
                                                } else {
                                                    setAddDefectiveForm({ ...addDefectiveForm, partId: "" });
                                                }
                                            }}
                                        >
                                            <option value="">-- Manual Entry / New Part --</option>
                                            {parts.map(p => (
                                                <option key={p.id} value={p.id}>
                                                    {p.name} {p.partNumber ? `(#${p.partNumber})` : ""} - {p.model}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label>Part Name</label>
                                        <input
                                            className="form-control"
                                            value={addDefectiveForm.name}
                                            onChange={e => setAddDefectiveForm({ ...addDefectiveForm, name: e.target.value, partId: "" })}
                                            placeholder="e.g. PCB, Compressor"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label>Part Number</label>
                                        <input
                                            className="form-control"
                                            value={addDefectiveForm.partNumber}
                                            onChange={e => setAddDefectiveForm({ ...addDefectiveForm, partNumber: e.target.value, partId: "" })}
                                            placeholder="e.g. CDCO-K825-00N"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label>Model / Spec</label>
                                        <input
                                            className="form-control"
                                            value={addDefectiveForm.model}
                                            onChange={e => setAddDefectiveForm({ ...addDefectiveForm, model: e.target.value, partId: "" })}
                                            placeholder="e.g. BO-IC318TKU"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label>Location</label>
                                        <select
                                            className="form-select"
                                            value={addDefectiveForm.location}
                                            onChange={e => setAddDefectiveForm({ ...addDefectiveForm, location: e.target.value, partId: "" })}
                                        >
                                            <option value="">-- Select --</option>
                                            {masterLocations.map(loc => (
                                                <option key={loc.id} value={loc.name}>{loc.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="fw-bold text-danger">Defective Quantity to Add</label>
                                        <input
                                            type="number"
                                            className="form-control border-danger fw-bold"
                                            value={addDefectiveForm.qty}
                                            onChange={e => setAddDefectiveForm({ ...addDefectiveForm, qty: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="fw-bold">Ticket / Call Number</label>
                                        <input
                                            className="form-control"
                                            value={addDefectiveForm.ticketNumber}
                                            onChange={e => setAddDefectiveForm({ ...addDefectiveForm, ticketNumber: e.target.value })}
                                            placeholder="Required"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="fw-bold">Customer Name</label>
                                        <input
                                            className="form-control"
                                            value={addDefectiveForm.customerName}
                                            onChange={e => setAddDefectiveForm({ ...addDefectiveForm, customerName: e.target.value })}
                                            placeholder="Required"
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label>Remarks</label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            value={addDefectiveForm.remarks}
                                            onChange={e => setAddDefectiveForm({ ...addDefectiveForm, remarks: e.target.value })}
                                        ></textarea>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowAddDefectiveModal(false)}>Cancel</button>
                                <button className="btn btn-danger" onClick={handleAddDefectiveStock}>Add to Defective Stock</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* EDIT MODAL (Generic) */}
            {showEditModal && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Entry</h5>
                                <button className="btn-close" onClick={() => setShowEditModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-3">
                                    {(view === "GOOD" || view === "DEFECTIVE") ? (
                                        <>
                                            <div className="col-12">
                                                <label>Part Name</label>
                                                <input className="form-control" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
                                            </div>
                                            <div className="col-6">
                                                <label>Part Number</label>
                                                <input className="form-control" value={editForm.partNumber} onChange={e => setEditForm({ ...editForm, partNumber: e.target.value })} />
                                            </div>
                                            <div className="col-6">
                                                <label>Model</label>
                                                <input className="form-control" value={editForm.model} onChange={e => setEditForm({ ...editForm, model: e.target.value })} />
                                            </div>
                                            <div className="col-12">
                                                <label>Location</label>
                                                <select
                                                    className="form-select"
                                                    value={editForm.location}
                                                    onChange={e => setEditForm({ ...editForm, location: e.target.value })}
                                                >
                                                    <option value="">-- Select Location --</option>
                                                    {masterLocations.map(loc => (
                                                        <option key={loc.id} value={loc.name}>{loc.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-6">
                                                <label>Good Qty</label>
                                                <input type="number" className="form-control" value={editForm.goodQty} onChange={e => setEditForm({ ...editForm, goodQty: e.target.value })} />
                                            </div>
                                            <div className="col-6">
                                                <label>Defective Qty</label>
                                                <input type="number" className="form-control" value={editForm.defectiveQty} onChange={e => setEditForm({ ...editForm, defectiveQty: e.target.value })} />
                                            </div>
                                            <div className="col-6">
                                                <label className="fw-bold">Purchase Price</label>
                                                <input
                                                    type="number"
                                                    className="form-control border-danger"
                                                    value={editForm.purchasePrice}
                                                    onChange={e => setEditForm({ ...editForm, purchasePrice: e.target.value })}
                                                />
                                            </div>
                                            <div className="col-6">
                                                <label className="fw-bold">Selling Price</label>
                                                <input
                                                    type="number"
                                                    className="form-control border-success"
                                                    value={editForm.sellingPrice}
                                                    onChange={e => setEditForm({ ...editForm, sellingPrice: e.target.value })}
                                                />
                                            </div>
                                            <div className="col-12 mt-2">
                                                <div className={`p-2 rounded text-center fw-bold ${(editForm.sellingPrice - editForm.purchasePrice) >= 0 ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                                                    Estimated Profit: ₹{(editForm.sellingPrice - editForm.purchasePrice).toLocaleString()}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="col-12">
                                                <label>Customer Name</label>
                                                <input className="form-control" value={editForm.customerName} onChange={e => setEditForm({ ...editForm, customerName: e.target.value })} />
                                            </div>
                                            <div className="col-6">
                                                <label>Ticket Number</label>
                                                <input className="form-control" value={editForm.ticketNumber} onChange={e => setEditForm({ ...editForm, ticketNumber: e.target.value })} />
                                            </div>
                                            <div className="col-6">
                                                <label>Quantity</label>
                                                <input type="number" className="form-control" value={editForm.qty} onChange={e => setEditForm({ ...editForm, qty: e.target.value })} />
                                            </div>
                                            {(view === "HISTORY") && (
                                                <>
                                                    <div className="col-6">
                                                        <label>Courier</label>
                                                        <input className="form-control" value={editForm.courierName} onChange={e => setEditForm({ ...editForm, courierName: e.target.value })} />
                                                    </div>
                                                    <div className="col-6">
                                                        <label>Tracking</label>
                                                        <input className="form-control" value={editForm.trackingNumber} onChange={e => setEditForm({ ...editForm, trackingNumber: e.target.value })} />
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                    <div className="col-12">
                                        <label>Remarks</label>
                                        <textarea className="form-control" value={editForm.remarks} onChange={e => setEditForm({ ...editForm, remarks: e.target.value })}></textarea>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={handleEditSave}>Update Changes</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* CONSUME MODAL */}
            {showConsumeModal && selectedPart && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title text-success">🔧 Consume Part for Breakdown</h5>
                                <button className="btn-close" onClick={() => setShowConsumeModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <p><strong>Part:</strong> {selectedPart.name} (Good Stock: {selectedPart.goodQty})</p>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label>Quantity Used</label>
                                        <input type="number" className="form-control" value={consumeForm.qty} onChange={e => setConsumeForm({ ...consumeForm, qty: e.target.value })} />
                                    </div>
                                    <div className="col-12">
                                        <label>Ticket / Call Number</label>
                                        <input className="form-control" placeholder="Required" value={consumeForm.ticketNumber} onChange={e => setConsumeForm({ ...consumeForm, ticketNumber: e.target.value })} />
                                    </div>
                                    <div className="col-12">
                                        <label>Customer Name</label>
                                        <input className="form-control" placeholder="Required" value={consumeForm.customerName} onChange={e => setConsumeForm({ ...consumeForm, customerName: e.target.value })} />
                                    </div>
                                    <div className="col-12">
                                        <label>Remarks</label>
                                        <textarea className="form-control" value={consumeForm.remarks} onChange={e => setConsumeForm({ ...consumeForm, remarks: e.target.value })}></textarea>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowConsumeModal(false)}>Cancel</button>
                                <button className="btn btn-success" onClick={handleConsumePart}>Confirm Usage</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PartStock;
